import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, RefreshCw } from 'lucide-react';
import DateRangePicker from '../components/admin/DateRangePicker';
import StatCards from '../components/admin/StatCards';
import VisitorChart from '../components/admin/VisitorChart';
import TopPages from '../components/admin/TopPages';
import Sources from '../components/admin/Sources';
import TechBreakdown from '../components/admin/TechBreakdown';
import EventsTable from '../components/admin/EventsTable';
import {
  getOverviewStats,
  getCurrentVisitors,
  getVisitorTrend,
  getTopPages,
  getSources,
  getBrowsers,
  getDevices,
  getTopEvents,
} from '../services/posthog-api';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [range, setRange] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState({
    stats: null,
    currentVisitors: 0,
    trend: [],
    trendInterval: 'day',
    topPages: [],
    sources: [],
    browsers: [],
    devices: [],
    events: [],
  });

  // Auth check
  useEffect(() => {
    if (sessionStorage.getItem('admin_auth') !== 'true') {
      navigate('/admin/login');
    }
  }, [navigate]);

  const fetchData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    else setRefreshing(true);

    try {
      const [stats, currentVisitors, trend, topPages, sources, browsers, devices, events] =
        await Promise.all([
          getOverviewStats(range),
          getCurrentVisitors(),
          getVisitorTrend(range),
          getTopPages(range),
          getSources(range),
          getBrowsers(range),
          getDevices(range),
          getTopEvents(range),
        ]);

      setData({
        stats,
        currentVisitors,
        trend: trend.data,
        trendInterval: trend.interval,
        topPages,
        sources,
        browsers,
        devices,
        events,
      });
    } catch (err) {
      console.error('Dashboard data fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [range]);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  // Refresh current visitors every 30 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const current = await getCurrentVisitors();
        setData((prev) => ({ ...prev, currentVisitors: current }));
      } catch (_) {}
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem('admin_auth');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <h1 className="text-lg font-bold text-gray-900">Norsk Frøhandel</h1>
              <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                Analyse
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => fetchData(false)}
                disabled={refreshing}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Oppdater"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <DateRangePicker value={range} onChange={setRange} />
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Logg ut
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Stat Cards */}
        <StatCards stats={data.stats} currentVisitors={data.currentVisitors} />

        {/* Visitor Chart */}
        <VisitorChart data={data.trend} interval={data.trendInterval} loading={loading} />

        {/* Two Column: Top Pages + Sources */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TopPages data={data.topPages} loading={loading} />
          <Sources data={data.sources} loading={loading} />
        </div>

        {/* Two Column: Tech Breakdown + Events */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TechBreakdown browsers={data.browsers} devices={data.devices} loading={loading} />
          <EventsTable data={data.events} loading={loading} />
        </div>
      </div>
    </div>
  );
}
